import React, { type ReactElement, useMemo } from "react"
import {
  anniversaryGiftDescription,
  anniversaryGiftHeading,
  footer,
  giftGrid,
  header,
  headerContainer,
  image,
  label,
  modalContainer,
  modalContent,
  overlayWrapper,
  terms,
  termsAndConditionsLink,
  tileContainer,
} from "./AnniversaryGiftTeaser.css"
import { OverlayDrawer } from "packages/ui-shared/components/OverlayDrawer"
import type { ApolloClient, InMemoryCache } from "@apollo/client"
import { gql, useQuery } from "@apollo/client"
import { createRequestContext, getClient } from "lib/apolloClient"
import { Button } from "packages/ui-shared/components/Button/Button"
import { CloseIcon } from "components/SvgIcons"
import { useLocale } from "hooks"
import { space } from "@shared/ui.design-system"

type Props = {
  isVisible: boolean
  setNextVisibility: (visible: boolean, callback?: () => void) => void
  children?: ReactElement | null
}

const GET_MY_MEMBERSHIP_INFO = gql`
  query me {
    me {
      membership {
        subscriptions {
          active {
            sku
            active
            status
            currentTermEnd
            dateCancelled
          }
        }
      }
    }
  }
`

const GET_ANNIVERSARY_GIFT_TEASER = gql`
  query getAnniversaryGiftModal {
    getAnniversaryGiftModal {
      modalHeader
      modalDescription
      termsAndConditions
      gifts {
        id
        image
        label
        slug
      }
    }
  }
`

const apolloClientOptions = () => ({
  client: getClient() as ApolloClient<InMemoryCache>,
  context: createRequestContext({ sessionStrategy: "START_NEW_ANONYMOUS" }),
})

export const AnniversaryGiftTeaser: React.FC<Props> = ({
  isVisible,
  setNextVisibility,
  children = null,
}) => {
  const locale = useLocale()
  const urlPrefix = locale === "en-US" ? "/us" : ""
  const { data: myMembership } = useQuery(
    GET_MY_MEMBERSHIP_INFO,
    apolloClientOptions()
  )

  const { data: anniversaryGiftsData } = useQuery(
    GET_ANNIVERSARY_GIFT_TEASER,
    apolloClientOptions()
  )

  const { gifts, modalHeader, modalDescription, termsAndConditions } =
    anniversaryGiftsData?.getAnniversaryGiftModal || {}

  const { currentTermEnd: membershipEnd } =
    myMembership?.me?.membership?.subscriptions?.active?.[0] || {}

  const daysUntilMembershipEnd = useMemo(() => {
    const membershipEndDate = new Date(membershipEnd)
    const today = new Date()
    const diffInMs = membershipEndDate.getTime() - today.getTime()
    return Math.ceil(diffInMs / (1000 * 60 * 60 * 24))
  }, [membershipEnd])

  const isValidUserContext = useMemo(() => {
    if (myMembership) {
      const { active, status, sku } =
        myMembership.me?.membership?.subscriptions?.active?.[0] || {}
      if (
        sku?.startsWith("BP_PLUS") &&
        active &&
        status === "active" &&
        daysUntilMembershipEnd
      ) {
        return daysUntilMembershipEnd <= 32
      }
      return false
    }
  }, [myMembership, daysUntilMembershipEnd])

  if (isValidUserContext && gifts?.length) {
    const daysAsText = daysUntilMembershipEnd === 1 ? "day" : "days"
    return (
      <OverlayDrawer isOpen={isVisible} className={overlayWrapper}>
        <div className={modalContainer}>
          <div className={modalContent}>
            <div className={header}>
              <div className={headerContainer}>
                <h2
                  className={anniversaryGiftHeading}
                >{`${modalHeader} ${daysUntilMembershipEnd} ${daysAsText}...`}</h2>
                <CloseIcon
                  cursor="pointer"
                  boxSize={space[24]}
                  aria-label="Close"
                  onClick={() => setNextVisibility(false)}
                />
              </div>

              <p className={anniversaryGiftDescription}>{modalDescription}</p>
            </div>
            <div className={giftGrid}>
              {gifts?.map((gift, idx) => (
                <a href={`${urlPrefix}/products/${gift.slug}`} key={idx}>
                  <div className={tileContainer}>
                    <img className={image} src={gift.image} alt={gift.title} />
                    <div className={label}>{gift.label}</div>
                  </div>
                </a>
              ))}
            </div>
          </div>
          <div className={footer}>
            <p className={terms}>
              {termsAndConditions}{" "}
              <a
                href={`${urlPrefix}/legal/terms-and-conditions`}
                className={termsAndConditionsLink}
              >
                T&C's apply
              </a>
            </p>
            <Button
              variant="primary"
              handleClick={() => setNextVisibility(false)}
            >
              Email me when it's here
            </Button>
            <Button
              variant="tertiary"
              handleClick={() => setNextVisibility(false)}
            >
              Close
            </Button>
          </div>
        </div>
      </OverlayDrawer>
    )
  }

  if (isValidUserContext === false) {
    return children
  }

  return null
}
